let csrfToken = '';

export function setCsrfToken(value: string): void {
  csrfToken = value;
}

export class ApiClientError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

export async function apiResponse(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (typeof init.body === 'string' && !headers.has('content-type')) headers.set('content-type', 'application/json');
  if (csrfToken && !['GET', 'HEAD'].includes((init.method ?? 'GET').toUpperCase())) headers.set('x-csrf-token', csrfToken);
  const response = await fetch(path, { ...init, headers, credentials: 'include' });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: { code: string; message: string } } | null;
    throw new ApiClientError(body?.error?.code ?? 'REQUEST_FAILED', body?.error?.message ?? '请求失败', response.status);
  }
  return response;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await apiResponse(path, init);
  const body = await response.json() as { data: T };
  return body.data;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

// Fetch does not expose upload progress. Keep this small XHR adapter here so
// uploads still receive the same CSRF, credentials, and normalized errors.
export async function apiUpload<T>(path: string, init: RequestInit = {}, onProgress?: (progress: UploadProgress) => void): Promise<T> {
  const headers = new Headers(init.headers);
  if (typeof init.body === 'string' && !headers.has('content-type')) headers.set('content-type', 'application/json');
  if (csrfToken && !['GET', 'HEAD'].includes((init.method ?? 'GET').toUpperCase())) headers.set('x-csrf-token', csrfToken);
  const signal = init.signal;
  if (signal?.aborted) throw new DOMException('请求已取消', 'AbortError');
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;
    const cleanup = () => { signal?.removeEventListener('abort', abort); };
    const finish = (handler: () => void) => { if (settled) return; settled = true; cleanup(); handler(); };
    const abort = () => xhr.abort();
    xhr.open(init.method ?? 'GET', path);
    xhr.withCredentials = true;
    headers.forEach((value, key) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress?.({ loaded: event.loaded, total: event.total }); };
    xhr.onload = () => {
      finish(() => {
        if (xhr.status < 200 || xhr.status >= 300) {
          let body: { error?: { code?: string; message?: string } } | null;
          try { body = JSON.parse(xhr.responseText) as { error?: { code?: string; message?: string } }; } catch { body = null; }
          reject(new ApiClientError(body?.error?.code ?? 'REQUEST_FAILED', body?.error?.message ?? '请求失败', xhr.status));
          return;
        }
        try {
          const body = xhr.responseText ? JSON.parse(xhr.responseText) as { data: T } : { data: undefined as T };
          resolve(body.data);
        } catch { reject(new ApiClientError('INVALID_RESPONSE', '服务响应格式无效', xhr.status)); }
      });
    };
    xhr.onerror = () => finish(() => reject(new Error('网络请求失败')));
    xhr.ontimeout = () => finish(() => reject(new Error('请求超时')));
    xhr.onabort = () => finish(() => reject(new DOMException('请求已取消', 'AbortError')));
    signal?.addEventListener('abort', abort, { once: true });
    xhr.send(init.body as XMLHttpRequestBodyInit | Document | null | undefined);
  });
}

export async function apiList<T>(path: string): Promise<T[]> {
  const items: T[] = [];
  let nextPath = path;
  for (let page = 0; page < 10_000; page += 1) {
    const result = await api<{ items: T[]; nextCursor: string | number | null }>(nextPath);
    items.push(...result.items);
    if (result.nextCursor === null || result.nextCursor === undefined) return items;
    const separator = path.includes('?') ? '&' : '?';
    nextPath = `${path}${separator}cursor=${encodeURIComponent(String(result.nextCursor))}`;
  }
  throw new ApiClientError('PAGINATION_LIMIT', '分页读取超过安全上限', 500);
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value);
}
